import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Search, Eye } from 'lucide-react';
import platformApi from '../api/platform';
import orgApi from '../api/organizations';

const PlatformOrganizations = () => {
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [switchingId, setSwitchingId] = useState(null);

  useEffect(() => {
    platformApi.listOrganizations()
      .then(data => {
        setOrgs(data.organizations || []);
        setLoading(false);
      })
      .catch(err => {
        setError(err.response?.data?.detail || 'Failed to load organizations');
        setLoading(false);
      });
  }, []);

  const filtered = orgs.filter(org =>
    org.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleView = async (orgId) => {
    setSwitchingId(orgId);
    try {
      await orgApi.switchOrg(orgId);
      window.location.href = '/';
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to switch organization');
      setSwitchingId(null);
    }
  };

  if (loading) {
    return <div className="p-8 text-gray-400">Loading organizations...</div>;
  }

  if (error && orgs.length === 0) {
    return <div className="p-8 text-red-400">{error}</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">Platform Organizations</h1>
        <p className="text-sm text-gray-500 mt-1">
          Browse and switch into any organization on the platform
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <input
          type="text"
          placeholder="Search by organization name..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 bg-navy-800 border border-navy-700 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent"
        />
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="text-center py-16">
          <Building2 className="w-12 h-12 mx-auto text-gray-600 mb-4" />
          <p className="text-gray-400 text-lg">No organizations found</p>
          <p className="text-gray-600 text-sm mt-1">
            {search ? 'Try adjusting your search' : 'No organizations have been created yet'}
          </p>
        </div>
      ) : (
        <div className="bg-navy-800 rounded-lg border border-navy-700 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-navy-700">
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Name</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Description</th>
                <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Members</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Created</th>
                <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Status</th>
                <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(org => (
                <tr key={org.id} className="border-b border-navy-700/50 hover:bg-navy-750 transition-colors">
                  <td className="px-4 py-3 text-sm text-white font-medium">{org.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-400 max-w-xs truncate">{org.description || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-400 text-center">{org.member_count}</td>
                  <td className="px-4 py-3 text-sm text-gray-400">{new Date(org.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      org.is_active
                        ? 'bg-green-400/10 text-green-400'
                        : 'bg-gray-400/10 text-gray-400'
                    }`}>
                      {org.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => handleView(org.id)}
                      disabled={switchingId === org.id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-cyan-400/10 text-cyan-400 rounded-md text-xs font-medium hover:bg-cyan-400/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      {switchingId === org.id ? 'Switching...' : 'View'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {error && orgs.length > 0 && (
        <div className="text-red-400 text-sm">{error}</div>
      )}
    </div>
  );
};

export default PlatformOrganizations;
